import React, { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Chart from 'recharts';

import { Card, Page, Switcher } from '@nearst/ui';

import DatePicker from '@components/DatePicker';
import * as Stats from '@components/StatsCard';
import LocationFilter from '../../insights/components/LocationFilter';
import OptionsButton from '../components/OptionsButton';
import ReportFilters from '../components/ReportFilters';
import NoData from '@components/Graphs/Placeholders/NoData';
import { TooltipContainer } from '@components/Graphs/Tooltip';
import { useStoredQuery } from '@services/insights';
import { useShop } from '@services/ShopProvider';
import findCommonPrefix from '@utils/findCommonPrefix';

import styles from './AdsImpact.module.scss';

export const Tooltip = ({ active, payload }) => {
	if (!active || !payload || !payload.length) {
		return null;
	}

	const { name, gbp_views, local_listings_views, local_ads_views } = payload[0].payload;

	return (
		<TooltipContainer>
			<strong>{name}</strong>
			<p>GBP views: {gbp_views.toLocaleString()}</p>
			<p>Local listings views: {local_listings_views.toLocaleString()}</p>
			{local_ads_views > 0 ? <p>Local Ads views: {local_ads_views.toLocaleString()}</p> : null}
		</TooltipContainer>
	);
};

export const AdsImpactContent = ({ options, shopIds }) => {
	const { data: shops } = useShop();
	const { data } = useStoredQuery('reports/ads-impact', { shopIds }, { suspense: true });

	const totalLocalAdsViews = data.reduce((acc, { local_ads_views }) => acc + local_ads_views, 0);
	const hasLocalAds = data.filter((row) => row.local_ads_views > 0).map((row) => row.total_views);
	const averageOrganicViews = data.reduce((acc, { organic_views }) => acc + organic_views, 0) / data.length;
	const averageViewsWithLocalAds = hasLocalAds.reduce((acc, views) => acc + views, 0) / hasLocalAds.length;

	const visibilityIncrease = ((averageViewsWithLocalAds - averageOrganicViews) / averageOrganicViews) * 100;

	const chartData = data.map((item, index) => ({
		...item,
		index,
		name: shops.find((shop) => shop.id === item.shop_id)?.name
	}));

	const commonPrefix = findCommonPrefix(chartData.map((item) => item.name));

	if (!hasLocalAds.length || !averageOrganicViews || !averageViewsWithLocalAds) {
		return <NoData text="No data available for the selected date range" />;
	}

	return (
		<Card.Section>
			<Switcher space="2rem">
				<Stats.Figure>
					{totalLocalAdsViews.toLocaleString()}
					<Stats.FigureDescription>Shoppers reached with Local Ads</Stats.FigureDescription>
				</Stats.Figure>
				<Stats.Figure>
					{Math.round(averageOrganicViews).toLocaleString()}
					<Stats.FigureDescription>Average organic views</Stats.FigureDescription>
				</Stats.Figure>
				<Stats.Figure>
					{Math.round(averageViewsWithLocalAds).toLocaleString()}
					<Stats.FigureDescription>Average location views with Local Ads</Stats.FigureDescription>
				</Stats.Figure>
				<Stats.Figure>
					{Math.round(visibilityIncrease).toLocaleString()}%
					<Stats.FigureDescription>Increase in views with Local Ads</Stats.FigureDescription>
				</Stats.Figure>
			</Switcher>

			<div className={styles.chartContainer}>
				<Chart.ResponsiveContainer
					width={options.summaryView ? 650 : '100%'}
					maxHeight={options.summaryView ? 600 : undefined}
					minHeight={(options.summaryView ? 18 : 27) * chartData.length + 96}
				>
					<Chart.ComposedChart
						data={chartData}
						margin={{ top: 48, left: 0, right: 48, bottom: 64 }}
						layout="vertical"
						id="chart-container"
					>
						<Chart.XAxis type="number" hide />
						<Chart.YAxis
							dataKey="index"
							tickFormatter={(index) => chartData[index]?.name.replace(commonPrefix, '').trim()}
							type="number"
							width={240}
							stroke="translate"
							tickCount={chartData.length}
							interval={options.summaryView && chartData.length > 50 ? 1 : 0}
							domain={['dataMin', 'dataMax']}
						/>
						<Chart.Bar
							stackId={1}
							dataKey="gbp_views"
							radius={4}
							barSize={options.summaryView ? 13 : 20}
							fill="var(--grey)"
							style={{ stroke: 'white', strokeWidth: 2 }}
						/>
						<Chart.Bar
							stackId={1}
							dataKey="local_listings_views"
							radius={4}
							fill="var(--green)"
							style={{ stroke: 'white', strokeWidth: 2 }}
						/>
						<Chart.Bar
							stackId={1}
							dataKey="local_ads_views"
							radius={4}
							fill="var(--blue)"
							style={{ stroke: 'white', strokeWidth: 2 }}
							label={
								options.dataLabels
									? {
											position: 'right',
											fill: 'var(--grey-darkest)',
											formatter: (v) => v.toLocaleString()
										}
									: undefined
							}
						/>
						{options.referenceLines && chartData.length > 1 && (
							<>
								<Chart.ReferenceLine
									stroke="var(--grey-darker)"
									ifOverflow="visible"
									segment={[
										{
											x: averageOrganicViews,
											y: -0.5
										},
										{
											x: averageOrganicViews,
											y: chartData.length - 0.5
										}
									]}
									label={{
										position: 'bottom',
										style: { fontWeight: 'bold' },
										value: 'Avg. organic views',
										fill: 'var(--grey-darkest)'
									}}
								/>
								<Chart.ReferenceLine
									stroke="var(--grey-darker)"
									ifOverflow="visible"
									segment={[
										{
											x: averageViewsWithLocalAds,
											y: -0.5
										},
										{
											x: averageViewsWithLocalAds,
											y: chartData.length
										}
									]}
									label={{
										position: 'bottom',
										style: { fontWeight: 'bold' },
										value: 'Avg. with Local Ads',
										fill: 'var(--grey-darkest)'
									}}
								/>
							</>
						)}
						<Chart.Tooltip cursor={false} content={Tooltip} />
					</Chart.ComposedChart>
				</Chart.ResponsiveContainer>
			</div>
		</Card.Section>
	);
};

export const AdsImpact = () => {
	const { data: shops } = useShop();

	const [shopIds, setShopIds] = useState(shops.map((shop) => shop.id.toString()));
	const [options, setOptions] = useState({
		dataLabels: true,
		referenceLines: true,
		summaryView: false
	});

	return (
		<>
			<Page.Header>
				<Link to="..">
					<Page.BackButton>Reports</Page.BackButton>
				</Link>
				<h1>Local Ads impact</h1>
				<ReportFilters>
					<DatePicker />
					<LocationFilter shops={shops} selectedShops={shopIds} setSelectedShops={setShopIds} />
					<OptionsButton
						options={[
							{
								id: 'dataLabels',
								label: 'Data labels'
							},
							{
								id: 'referenceLines',
								label: 'Average lines'
							},
							{
								id: 'summaryView',
								label: 'Summary view'
							}
						]}
						values={options}
						setValues={setOptions}
					/>
				</ReportFilters>
			</Page.Header>
			<Page.Section>
				<Card sectioned>
					<Suspense fallback={<Card.Loader message="Generating report..." />}>
						<AdsImpactContent options={options} shopIds={shopIds} />
					</Suspense>
				</Card>
			</Page.Section>
		</>
	);
};
